import { parse, stringify } from 'query-string';
import React from 'react';

import HeadMeta from '@/components/static-pages/HeadMeta';
import LocalityTemplate from '@/components/templates/LocalityTemplate';
import StateLocalityTemplate from '@/components/templates/StateLocalityTemplate';
import { ESearchFilters } from '@/constants/searchFilters';
import { SEO_VEHICLES } from '@/constants/vehicleDetails';
import apiRequest from '@/services/apiRequest';
import { InitSerpGuidedSearchDecision } from '@/services/experiments/serpGuidedSearch';
import { getLocalityRentalReviews } from '@/services/getLocalityRentalReviews';
import getPageData from '@/services/getPageData.js';
import { getSearchApi } from '@/utility/getCoreApi';

const testGroup = [
  '/rv-rental/nevada/las-vegas',
  '/rv-rental/texas/houston',
  '/rv-rental/california/san-francisco',
  '/rv-rental/hawaii/maui',
  '/rv-rental/texas/dallas',
  '/rv-rental/florida/jacksonville',
  '/rv-rental/new-mexico/albuquerque',
  '/rv-rental/california/orange-county',
  '/rv-rental/arizona/mesa',
  '/rv-rental/oregon/portland',
];

const Locality = props => {
  const { keyword, metadata } = props;
  const { seo_title, seo_description, locality_type, title, region_code, canonical_url } = metadata;

  const rvType = SEO_VEHICLES[keyword?.slug] ? keyword.title : 'RV';
  const canonicalUrl = (rvType !== 'RV' && keyword?.url) || canonical_url;
  const keywordSeoTitle = `${rvType} Rental ${title}, ${region_code}`;
  const keywordSeoDescription = `Discover the best ${rvType} Rental options in ${title}, ${region_code}! Find more Class A, Class C, Class B, trailers, fifth wheel trailers and more at Outdoorsy!`;
  const seoTitle = keyword ? keywordSeoTitle : seo_title;
  const seoDescription = keyword ? keywordSeoDescription : seo_description;
  return (
    <React.Fragment>
      <HeadMeta
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
        noindex={!!props.fallback}
        hideOutdoorsyTitle={!!props.isTestGroup}
      />

      {/** Initialize this experiment just for counties for now */}
      {locality_type === 'county' && <InitSerpGuidedSearchDecision />}
      {(locality_type === 'state' && <StateLocalityTemplate {...props} />) || (
        <LocalityTemplate {...props} />
      )}
    </React.Fragment>
  );
};

Locality.getInitialProps = async ({ req, res, query }) => {
  const pageData = await getPageData({ req, res, query });
  const { bounds } = pageData.metadata;

  let localitySearchFilters = {
    [ESearchFilters.RADIUS]: 10,
    [ESearchFilters.AUTO_EXPAND_RADIUS]: true,
  };

  const activeKeyword = query.keyword
    ? pageData.metadata?.keywords?.find?.(key => key.slug == query.keyword)
    : null;

  if (activeKeyword) {
    pageData.metadata.breadcrumbs = [
      ...pageData.metadata.breadcrumbs,
      {
        title: activeKeyword.title,
        url: activeKeyword.url,
      },
    ];

    localitySearchFilters = {
      ...localitySearchFilters,
      ...activeKeyword.filters,
    };
  }

  const boundsFormatted =
    bounds && pageData.metadata.locality_type === 'county'
      ? {
          'bounds[ne]': `${bounds['ne']['lat']},${bounds['ne']['lon']}`,
          'bounds[sw]': `${bounds['sw']['lat']},${bounds['sw']['lon']}`,
        }
      : null;

  if (boundsFormatted) {
    pageData.metadata.boundsFormatted = boundsFormatted;
  }

  const rentalReviewsBounds = bounds
    ? {
        lat: bounds.ne.lat,
        lng: bounds.ne.lon,
      }
    : null;

  if (rentalReviewsBounds) {
    const localityRentalReviews = await getLocalityRentalReviews(rentalReviewsBounds);
    pageData.metadata.localityRentalReviews = localityRentalReviews;
  }

  // Alter sections and add search query data to not fetch it on the client
  pageData.metadata.sections = await Promise.all(
    (pageData.metadata.sections || []).map(section => {
      if (section?.section_type === 'search_query' && section?.search_query_url) {
        return new Promise(resolve => {
          try {
            apiRequest({
              url: `${getSearchApi()}/rentals?${stringify({
                ...parse(new URL(section.search_query_url).search),
                remove: 'images',
                raw_json: true,
                omit_aggregation: true,
                [ESearchFilters.PAGE_OFFSET]: 0,
                [ESearchFilters.PAGE_LIMIT]: 4,
              })}`,
            })
              .then(r => resolve({ ...section, search_query_data: r.data }))
              .catch(() => resolve({ ...section, search_query_data: [] }));
          } catch {
            resolve({ ...section, search_query_data: [] });
          }
        });
      }

      return section;
    }),
  );

  // Filter out keywords that have a search query section with the same url.
  pageData.metadata.keywords = (pageData.metadata.keywords || []).filter(k => {
    return !pageData.metadata.sections.some(section => {
      return section?.section_type === 'search_query' && section?.button_url === k.url;
    });
  });

  return {
    isTestGroup: testGroup.includes(pageData.metadata.url_path),
    ...pageData,
    keyword: activeKeyword,
    localitySearchFilters,
  };
};

export default Locality;
